<template>
  <div class="units-search-filters">
    <b-button
      variant="outline-secondary"
      size="sm"
      class="d-inline-flex align-items-center"
      :disabled="disabled"
      @click="showModal = true"
    >
      <feather-icon
        icon="FilterIcon"
        size="16"
        class="mr-50"
      />
      <span>Filters</span>
      <b-badge
        v-if="hasActiveFilters"
        variant="primary"
        class="ml-50"
      >
        Active
      </b-badge>
      <b-spinner
        v-if="isTableLoading"
        small
        class="ml-50"
        label="Loading..."
      />
    </b-button>

    <b-modal
      id="units-search-filters-modal"
      v-model="showModal"
      title="Filters"
      size="md"
      hide-footer
    >
      <b-form>
        <b-form-group
          v-if="companyPurpose !== 'rent'"
          label="Category"
          label-for="units-filter-category"
        >
          <ImageTextSelect
            id="units-filter-category"
            :model-value="filterCategoryId"
            :options="categoryFilterOptions"
            :disabled="disabled"
            @update:model-value="$emit('update:filterCategoryId', $event)"
          />
        </b-form-group>

        <b-form-group
          v-if="companyPurpose !== 'rent'"
          label="Stock location"
          label-for="units-filter-stock-location"
        >
          <ImageTextSelect
            id="units-filter-stock-location"
            :model-value="filterStockLocationId"
            :options="stockLocationFilterOptions"
            :disabled="disabled"
            @update:model-value="$emit('update:filterStockLocationId', $event)"
          />
        </b-form-group>

        <b-form-group
          v-if="companyPurpose !== 'rent'"
          label="Stock below"
          label-for="units-filter-stock-below"
        >
          <b-form-input
            id="units-filter-stock-below"
            :value="filterStockBelow"
            placeholder="Stock below"
            type="number"
            min="0"
            step="1"
            :disabled="disabled"
            @input="$emit('update:filterStockBelow', normalizeInput($event))"
            @keypress="blockIntegerFilterKey"
          />
        </b-form-group>

        <b-form-group
          label="Search by keyword"
          label-for="units-filter-search"
        >
          <BarcodeTargetField
            :show-icon="showBarcodeTarget"
            hint-key="products-filters-search"
            :active="showModal"
          >
            <b-form-input
              id="units-filter-search"
              :value="filterKeyword"
              placeholder="keyword"
              :class="{ 'barcode-target': showBarcodeTarget }"
              :disabled="disabled"
              @input="$emit('update:filterKeyword', normalizeInput($event))"
            />
          </BarcodeTargetField>
        </b-form-group>
      </b-form>

      <div class="d-flex justify-content-between align-items-center mt-1">
        <div class="units-search-filters__status text-muted small d-flex align-items-center">
          <b-spinner
            v-if="showFooterLoading"
            small
            label="Loading..."
          />
          <span v-else-if="statusMessage">{{ statusMessage }}</span>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-1"
            :disabled="disabled || !hasActiveFilters"
            @click="$emit('reset')"
          >
            Reset
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="showModal = false"
          >
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'
import BarcodeTargetField from '@/views/Components/Elements/BarcodeTargetField.vue'

export default {
  name: 'UnitsSearchFilters',
  components: {
    BBadge,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BModal,
    BSpinner,
    ImageTextSelect,
    BarcodeTargetField,
  },
  props: {
    filterCategoryId: {
      default: null,
    },
    filterStockLocationId: {
      default: null,
    },
    filterStockBelow: {
      type: [String, Number],
      default: '',
    },
    filterKeyword: {
      type: String,
      default: '',
    },
    categoryFilterOptions: {
      type: Array,
      default: () => [],
    },
    stockLocationFilterOptions: {
      type: Array,
      default: () => [],
    },
    companyPurpose: {
      type: String,
      default: '',
    },
    showBarcodeTarget: {
      type: Boolean,
      default: false,
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
    isTableLoading: {
      type: Boolean,
      default: false,
    },
    filteredCount: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:filterCategoryId',
    'update:filterStockLocationId',
    'update:filterStockBelow',
    'update:filterKeyword',
    'reset',
  ],
  data() {
    return {
      showModal: false,
      isFilterLoading: false,
      showResultStatus: false,
    }
  },
  computed: {
    entityLabel() {
      return this.companyPurpose === 'rent' ? 'unit' : 'product'
    },
    filterSignature() {
      return [
        this.filterCategoryId,
        this.filterStockLocationId,
        this.filterStockBelow,
        this.filterKeyword,
        this.hasActiveFilters,
      ].join('|')
    },
    showFooterLoading() {
      return this.showModal
        && this.hasActiveFilters
        && (this.isFilterLoading || this.isTableLoading)
    },
    statusMessage() {
      if (!this.showResultStatus || !this.hasActiveFilters || this.showFooterLoading) {
        return ''
      }

      const count = Number(this.filteredCount) || 0
      if (count === 1) {
        return `Found one ${this.entityLabel}`
      }

      return `Found ${count} ${this.entityLabel}s`
    },
  },
  watch: {
    filterSignature() {
      if (!this.hasActiveFilters) {
        this.isFilterLoading = false
        this.showResultStatus = false
        return
      }

      if (!this.showModal) {
        return
      }

      this.isFilterLoading = true
      this.showResultStatus = false
    },
    isTableLoading(loading) {
      if (!this.showModal || !this.hasActiveFilters) {
        return
      }

      if (loading) {
        this.isFilterLoading = true
        this.showResultStatus = false
        return
      }

      this.isFilterLoading = false
      this.showResultStatus = true
    },
    filteredCount() {
      if (!this.showModal || !this.hasActiveFilters || this.isTableLoading) {
        return
      }

      this.isFilterLoading = false
      this.showResultStatus = true
    },
  },
  methods: {
    normalizeInput(value) {
      return value && typeof value === 'object' && value.target
        ? value.target.value
        : value
    },
    blockIntegerFilterKey(event) {
      const { key } = event

      if (
        key === 'Backspace'
        || key === 'Delete'
        || key === 'Escape'
        || event.ctrlKey
        || event.metaKey
      ) {
        return
      }

      if (key === 'Enter' || key === 'Tab' || key.startsWith('Arrow')) {
        return
      }

      if (key.length === 1 && /\d/.test(key)) {
        return
      }

      event.preventDefault()
    },
  },
}
</script>
